import React from "react";
import clsx from "clsx";
import get from "lodash/get";

import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import { Close } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { isFutureDate, transformViewDate } from "utils/date-utils";
import { useRacehorse360Api } from "hooks/api";
import Loader from "components/Loader";
import Breakpoints from "common/breakpoints";
import { SortOrder } from "interfaces/SortOrder";
import useStyles from "./styles";

interface IProps {
  onClose: () => void;
  horse: racehorse360.IHorse;
}

interface IColumn {
  id: string;
  title: string;
  field: string;
  shouldDisplay: boolean;
  renderer?: (value: string) => void;
}

const VetListsPopUp = (props: IProps) => {
  const { onClose, horse } = props;
  const classes = useStyles();
  const theme = useTheme();
  const matchesDownXS420 = useMediaQuery(
    theme.breakpoints.down(Breakpoints.XS_420)
  );

  const columns: IColumn[] = [
    {
      id: "ListType",
      title: `List Type | ${matchesDownXS420 ? "Track - " : ""}Note`,
      field: "listType",
      shouldDisplay: true
    },
    {
      id: "Track",
      title: "Track",
      field: "facility.code",
      shouldDisplay: !matchesDownXS420
    },
    {
      id: "DateOn",
      title: "Date On",
      field: "dateOn",
      shouldDisplay: true,
      renderer: val => (val ? transformViewDate(val) : "-")
    },
    {
      id: "DateOff",
      title: "Date Off",
      field: "dateOff",
      shouldDisplay: true,
      renderer: val =>
        val ? (
          <span className={clsx({ [classes.italicCell]: isFutureDate(val) })}>
            {transformViewDate(val)}
          </span>
        ) : (
          "-"
        )
    }
  ];

  const { useListHorseOnLists } = useRacehorse360Api();

  const { data: horseOnListsData, isLoading: isHorseOnListsLoading } =
    useListHorseOnLists({
      query: {
        horseId: { value: horse.id }
      },
      getOptions: {
        select: [
          "id",
          "list_type",
          "reason",
          "date_on",
          "dateOff",
          "comment",
          "facility.code"
        ],
        orderBy: [`dateOff ${SortOrder.DESC}`, `dateOn ${SortOrder.DESC}`]
      }
    });
  const horseOnLists: racehorse360.IHorseOnList[] =
    horseOnListsData?.horseOnLists || [];

  const renderTableHeader = () => {
    return (
      <div className={classes.tableHeader}>
        {columns.map(
          (column, index) =>
            column.shouldDisplay && (
              <span
                key={column.id}
                className={clsx(classes.cell, classes.headCell, {
                  [`${classes.centerCell}`]: index
                })}
              >
                {column.title}
              </span>
            )
        )}
      </div>
    );
  };

  const renderTableContent = () => {
    return (
      <div className={classes.tableContent}>
        {horseOnLists.map(list => (
          <div key={list.id} className={classes.tableRow}>
            <div
              className={clsx(classes.tableRowContent, {
                [classes.alert]: !list.dateOff || isFutureDate(list.dateOff)
              })}
            >
              {columns.map(
                (column, index) =>
                  column.shouldDisplay && (
                    <span
                      key={column.id}
                      className={clsx(classes.cell, {
                        [`${classes.centerCell}`]: index
                      })}
                    >
                      {column.renderer
                        ? column.renderer(get(list, column.field))
                        : get(list, column.field) || "-"}
                    </span>
                  )
              )}
            </div>
            {list.reason && (
              <div className={classes.reason}>
                {matchesDownXS420 && list.facility.code
                  ? `${list.facility.code} - ${list.reason}`
                  : list.reason}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTable = () => (
    <>
      {renderTableHeader()}
      {renderTableContent()}
    </>
  );

  return (
    <Dialog
      open={true}
      classes={{
        paper: classes.paper
      }}
      onClose={onClose}
    >
      <DialogTitle className={classes.muiDialogTitle}>
        <div className={classes.dialogHeader}>
          <span>Vet List History</span>
          <IconButton onClick={onClose} className={classes.closeIcon}>
            <Close />
          </IconButton>
        </div>
        <div className={classes.horseName}>{horse.name}</div>
      </DialogTitle>

      <DialogContent className={classes.muiDialogContent}>
        {isHorseOnListsLoading ? <Loader /> : renderTable()}
      </DialogContent>
    </Dialog>
  );
};

export default VetListsPopUp;
